import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Input, Modal, Segmented } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createDoctorInstruction } from './api';
import type { DoctorInstructionPriority } from './types';

/**
 * An instruction the doctor sends about a visit.
 *
 * Filed as PROCESSING and nothing more: it does not reach the caregiver until
 * the office marks it HANDLED. The button says "send" rather than anything that
 * implies it is already in effect, because a doctor who believes they have
 * scheduled work is the one failure this screen can actually cause.
 */

type Props = {
  open: boolean;
  visitId: number | null;
  onClose: () => void;
};

const PRIORITIES: DoctorInstructionPriority[] = ['LOW', 'NORMAL', 'HIGH'];

const DoctorInstructionModal = ({ open, visitId, onClose }: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [priority, setPriority] = useState<DoctorInstructionPriority>('NORMAL');

  useEffect(() => {
    if (open) {
      setTitle('');
      setDetails('');
      setPriority('NORMAL');
    }
  }, [open]);

  const save = useMutation({
    mutationFn: () =>
      createDoctorInstruction({
        visitId: visitId as number,
        title: title.trim(),
        details: details.trim() || null,
        priority,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['doctor', 'visit-instructions', visitId] });
      message.success(t('doctor-instruction-sent'));
      onClose();
    },
    onError: () => message.error(t('doctor-instruction-failed')),
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t('doctor-instruction-new-heading')}
      okText={t('doctor-instruction-submit')}
      cancelText={t('common-cancel')}
      onOk={() => save.mutate()}
      okButtonProps={{ disabled: !title.trim(), loading: save.isPending }}
      width={560}
      style={{ maxWidth: 'calc(100vw - 24px)' }}
      destroyOnHidden
    >
      <div className="flex flex-col gap-3">
        <div>
          <div className="text-[12px] text-slate-500 mb-1">{t('doctor-instruction-title-label')}</div>
          <Input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={t('doctor-instruction-title-placeholder')}
          />
        </div>
        <div>
          <div className="text-[12px] text-slate-500 mb-1">{t('doctor-instruction-priority-label')}</div>
          <Segmented
            value={priority}
            onChange={(value) => setPriority(value as DoctorInstructionPriority)}
            options={PRIORITIES.map((value) => ({
              value,
              label: t(`doctor-instruction-priority-${value.toLowerCase()}`),
            }))}
          />
        </div>
        <div>
          <div className="text-[12px] text-slate-500 mb-1">{t('doctor-instruction-details-label')}</div>
          <Input.TextArea rows={3} value={details} onChange={(event) => setDetails(event.target.value)} />
        </div>
      </div>
    </Modal>
  );
};

export default DoctorInstructionModal;
